import React, { useEffect, useState } from "react";
import { XCircle } from "react-feather";
import { defaultTableOption, rowPerPage } from "constants/table";
import CustomDataTable from "components/customs/table";
import { fetchAllAgencies, fetchTable } from "services/services.projects";
import { getFormattedDateString } from "utility/helpers";
import { Col } from "reactstrap";
import SearchBarComponent from "components/customs/searchbarV2";
import CommonModal from "components/common/modal";
import FormGenerator from "components/customs/form-generator/input";
import styled from "styled-components";
import { HeaderText } from "assets/js/Styles";
import logColumns from "./columns";

const ModalContainer = styled.div`
  margin: 1rem;

  textarea {
    width: 100%;
    border: none;
    background: transparent;
  }
`;

const ModalBody = styled.div`
  margin-top: 2rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;

  @media (max-width: 575.98px) {
    flex-direction: column;
  }
`;

const RowDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
`;

const ModalHeader = styled.div`
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  margin-top: 0.5rem;
`;

const SearchbarWrapper = styled.div`
  width: 25%;
  position: relative;
  justify-self: flex-end;

  input {
    border: 2px solid ${({ theme }) => theme.colors.black60};
    background: white;
    box-shadow: none;
    border-radius: 9px;
    height: clamp(35px, 5vw, 50px);
  }
`;

const HeaderTextSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin-bottom: 0;
  }

  a {
    text-decoration: none;
  }

  @media (max-width: 575.98px) {
    a {
      display: flex;
      justify-content: flex-end;
    }

    padding-top: 4rem;
  }
`;

const TableSection = styled.div`
  margin-top: 16px;
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const CardSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const CloseButtonSection = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const LogsTable = () => {
  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });
  const [isModalShown, setIsModalShown] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [renderBody, setRenderBody] = useState("");

  const toggleModal = () => setIsModalShown(!isModalShown);

  let changeSearchTimeoutID;

  const fetchDataTable = () => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    fetchTable({
      url: "/logs",
      params: {
        limit: tableOption.currentRowsPerPage,
        offset: tableOption.offset,
        search: tableOption.searchValue,
        column: tableOption.sort.field,
        order: tableOption.sort.direction,
      },
      callback: (data) => {
        setTableOption({
          ...tableOption,
          isLoading: false,
          data: data.rows,
          totalRows: data.count,
        });
      },
    });
  };

  const handleViewModal = (row) => {
    const agency = agencies?.find((ag) => ag.name === row.logInfo.agency);

    const body = (
      <>
        <CloseButtonSection>
          <a
            href="!#"
            onClick={(e) => {
              toggleModal();
              setIsModalShown(false);
              e.preventDefault();
            }}
          >
            <XCircle />
          </a>
        </CloseButtonSection>
        <ModalContainer>
          <ModalHeader>
            <h3>{"Message"}</h3>
            <h5>{`#${row.id}`}</h5>
          </ModalHeader>
          <ModalBody>
            <Col
              xs={12}
              md={6}
            >
              <RowDetails>
                <p>{"Recipient:"}</p>
                <p>{row.logInfo.recipient}</p>
              </RowDetails>
              <RowDetails>
                <p>{"Contact No:"}</p>
                <p>{row.to}</p>
              </RowDetails>
              <RowDetails>
                <p>{"Agency:"}</p>
                <p>{agency?.acronym}</p>
              </RowDetails>
            </Col>
            <Col
              xs={12}
              md={6}
            >
              <RowDetails>
                <p>{"Date:"}</p>
                <p>{getFormattedDateString(row.sentAt, "MMM dd, yyyy")}</p>
              </RowDetails>
              <RowDetails>
                <p>{"Time:"}</p>
                <p>{getFormattedDateString(row.sentAt, "HH:mm a")}</p>
              </RowDetails>
              <RowDetails>
                <p>{"Amount:"}</p>
                <p>{row.logInfo.amount}</p>
              </RowDetails>
            </Col>
          </ModalBody>

          <div>
            <FormGenerator
              isDisabled
              value={row.message}
              type="textarea"
              rows={20}
            />
          </div>
        </ModalContainer>
      </>
    );
    setRenderBody(body);
    toggleModal();
  };

  const columns = logColumns({
    agencies,
    handleViewModal,
  });

  const onChangePage = (page) => {
    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 100,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value;

      setTableOption({
        ...tableOption,
        page: 1,
        searchValue: eventValue.trim(),
      });
    }, 1000);
  };

  const onSort = (column, sortDirection) => {
    setTableOption({
      ...tableOption,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    });
  };

  const fetchAgencies = () => {
    fetchAllAgencies({
      callback: (data) => {
        setAgencies(data);
      },
    });
  };

  useEffect(() => {
    fetchAgencies();
  }, []);

  useEffect(() => {
    fetchDataTable();
  }, [
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.searchValue,
    tableOption.sort.field,
    tableOption.sort.direction,
  ]);

  return (
    <Wrapper>
      <CardSection>
        <CardContainer>
          <HeaderTextSection>
            <HeaderText>{"Notification Logs"}</HeaderText>

            <SearchbarWrapper>
              <SearchBarComponent
                onChangeSearch={onChangeSearch}
                isDisabled={tableOption?.isLoading}
              />
            </SearchbarWrapper>
          </HeaderTextSection>

          <TableSection>
            <CustomDataTable
              columns={columns}
              data={tableOption.data}
              noHeader
              progressPending={tableOption.isLoading}
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onSort={onSort}
              height="calc(100vh - 200px)"
              paginationServer
              sortServer
              paginationPerPage={tableOption.currentRowsPerPage}
              paginationTotalRows={tableOption.totalRows}
            />
          </TableSection>
        </CardContainer>
      </CardSection>
      <CommonModal
        isOpen={isModalShown}
        handleClose={toggleModal}
        renderBody={renderBody}
        modalSize="lg"
      />
    </Wrapper>
  );
};

export default LogsTable;
